import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getExaminationsList } from '../../../features/examinations/examinationSlice';
import { getSubjectsList } from '../../../features/subjects/subjectsSlice';
import { getClasses } from '../../../features/classes/ClassSlice';
import { getLabelsByInstituteType } from '../../../utils/constants';

// Import the smaller components
import ScheduleHeader from './ScheduleHeader';
import ScheduleFilters from './ScheduleFilters';
import ScheduleTable from './ScheduleTable';
import ScheduleFormDialog from './ScheduleFormDialog';
import ConfirmationDialog from '../../UIElements/ConfirmationDialog';
import ToastNotification from '../../UIElements/ToastNotification';
import useNotification from '../../../hooks/useNotification';
import {
  createExamSchedule,
  deleteExamSchedule,
  fetchPassPercentage,
  getExamSchedulesList,
  updateExamSchedule,
} from '../../../features/schedule/examScheduleSlice';
import PrintPreviewSchedule from './PrintPreviewSchedule';
import PassPercentConfiguration from '../PassPercentConfiguration';

const ExamSchedule = () => {
  const dispatch = useDispatch();
  const { notification, showSuccess, showError, hideNotification } =
    useNotification();

  // State management
  const [examSchedules, setExamSchedules] = useState([]);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [currentFilters, setCurrentFilters] = useState({
    department: 'All',
    class: 'All',
    section: 'All',
  });
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
  });
  const [passPercentage, setPassPercentage] = useState(35);
  // Data for dropdowns
  const [examTypes, setExamTypes] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [printPreviewScheduleOpen, setPrintPreviewScheduleOpen] =
    useState(false);
  const [printPreviewSchedule, setPrintPreviewSchedule] = useState(null);
  // Get institute type for dynamic labels
  const { instituteType } = useSelector((state) => {
    return state.auth?.successData?.userDetails ?? {};
  });
  const { Department } = getLabelsByInstituteType(instituteType);

  // Utility functions
  const formatTime = (time) => {
    if (!time) return '-';
    const d = new Date(`2000-01-01T${time}`);
    if (isNaN(d)) return time;
    return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false, // Use 24-hour format
    });
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d)) return date;
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Load initial data
  useEffect(() => {
    getFilters();
    getSchedules();
  }, [dispatch]);

  const getFilters = async () => {
    try {
      const examTypesRes = await dispatch(getExaminationsList()).unwrap();
      const subjectsRes = await dispatch(getSubjectsList()).unwrap();
      const departmentRes = await dispatch(getClasses()).unwrap();

      setExamTypes(examTypesRes || []);
      setSubjects(subjectsRes || []);
      setDepartments(departmentRes || []);
    } catch (error) {
      console.error('Error loading filters:', error);
    }
  };

  const getSchedules = async () => {
    try {
      const schedulesRes = await dispatch(getExamSchedulesList()).unwrap();
      setExamSchedules(schedulesRes || []);
    } catch (error) {
      console.error('Error loading schedules:', error);
    }
  };

  // Conflict detection
  const hasConflictWithExisting = (candidate, excludeId = null) => {
    if (!candidate.date || !candidate.classesName) return false;
    return examSchedules.some((s) => {
      if (excludeId && s.id === excludeId) return false;
      if (s.date !== candidate.date) return false;
      if (s.classesName !== candidate.classesName) return false;

      // If either schedule lacks time, don't treat as conflict
      if (
        !s.startTime ||
        !s.endTime ||
        !candidate.startTime ||
        !candidate.endTime
      )
        return false;
      if (
        s.departmentName === candidate.departmentName &&
        s.classesName === candidate.classesName
      )
        return (
          (candidate.startTime >= s.startTime &&
            candidate.startTime < s.endTime) ||
          (candidate.endTime > s.startTime && candidate.endTime <= s.endTime) ||
          (candidate.startTime <= s.startTime && candidate.endTime >= s.endTime)
        );
    });
  };

  // Schedule management functions
  const handleAddSchedule = async (scheduleData) => {
    try {
      await dispatch(
        createExamSchedule({
          ...scheduleData,
          sectionName: scheduleData.sectionName || 'test',
        }),
      ).unwrap();
      //setExamSchedules([...examSchedules, result]);
      getSchedules();
      setOpenDialog(false);
      showSuccess('Exam schedule has been successfully added.');
      setClasses([]);
      setSections([]);
    } catch (error) {
      console.error('Error adding schedule:', error);
      if (error == 'duplicate') {
        showError(' Subject  already exists');
      } else {
        showError('Failed to add exam schedule. Please try again.');
      }
    }
  };

  const handleUpdateSchedule = async (scheduleData) => {
    try {
      await dispatch(
        updateExamSchedule({ ...scheduleData, id: editingSchedule.id }),
      ).unwrap();

      setExamSchedules(
        examSchedules.map((s) =>
          s.id === editingSchedule.id
            ? { ...scheduleData, id: editingSchedule.id }
            : s,
        ),
      );
      setEditingSchedule(null);
      showSuccess('Exam schedule has been successfully updated.');
      setOpenDialog(false);
    } catch (error) {
      console.error('Error updating schedule:', error);
      if (error == 'duplicate') {
        showError('Duplicate subjects are not Accepted');
      } else {
        showError('Failed to update exam schedule. Please try again.');
      }
    }
  };

  const handleEditSchedule = (schedule) => {
    //handleFormDepartmentChange(schedule.departmentName);
    //handleFormClassChange(schedule.classesName);
    const dept = departments.find(
      (d) => d.department === schedule.departmentName,
    );
    const selectedClass = dept?.classes?.find(
      (cls) => cls.className === schedule.classesName,
    );
    setEditingSchedule(schedule);
    setClasses(dept.classes);
    setSections(selectedClass.sections);
    setDialogMode('edit');
    setOpenDialog(true);
  };

  const handleDeleteSchedule = (id) => {
    setConfirmationDialog({
      open: true,
      title: 'Delete Exam Schedule',
      message:
        'Are you sure you want to delete this exam schedule? This action cannot be undone.',
      onConfirm: () => confirmDeleteSchedule(id),
    });
  };

  const confirmDeleteSchedule = async (id) => {
    try {
      await dispatch(deleteExamSchedule(id)).unwrap();
      setExamSchedules(examSchedules.filter((s) => s.id !== id));
      showSuccess('Exam schedule has been successfully deleted.');
    } catch (error) {
      console.error('Error in deleting schedule:', error);
      if (error == 'marks mapped') {
        showError("Marks are mapped, so it can't be deleted");
      } else {
        showError('Failed to delete exam schedule. Please try again.');
      }
    } finally {
      setConfirmationDialog({
        open: false,
        title: '',
        message: '',
        onConfirm: null,
      });
    }
  };

  const openAddDialog = () => {
    setDialogMode('add');
    setEditingSchedule(null);
    setOpenDialog(true);
  };

  const handleSearch = (filterData) => {
    setCurrentFilters(filterData);
  };

  const handleClearFilters = () => {
    setCurrentFilters({
      department: 'All',
      class: 'All',
      section: 'All',
    });
  };

  const handleFormDepartmentChange = (departmentValue) => {
    // Update classes when department changes in form
    const dept = departments.find((d) => d.department === departmentValue);
    setClasses(dept?.classes || []);
    setSections([]); // Clear sections when department changes
  };

  const handleFormClassChange = (classValue) => {
    // Update sections when class changes in form
    const selectedClass = classes.find((cls) => cls.className === classValue);
    setSections(selectedClass?.sections || []);
  };

  // Filter schedules based on current filters
  const filteredSchedules = examSchedules.filter((s) => {
    const deptMatch =
      currentFilters.department === 'All' ||
      s.departmentName === currentFilters.department;
    const classMatch =
      currentFilters.class === 'All' || s.classesName === currentFilters.class;
    const sectionMatch =
      currentFilters.section === 'All' ||
      s.sectionName === currentFilters.section;
    return deptMatch && classMatch && sectionMatch;
  });

  const downloadSchedule = (group) => {
    setPrintPreviewSchedule(group);
    setPrintPreviewScheduleOpen(true);
  };
  //get Pass Percentage
  async function getPercentage(percent) {
    try {
      const response = await dispatch(fetchPassPercentage(percent)).unwrap();

      setPassPercentage(response?.passPercentage);
      percent
        ? showSuccess('Pass Percentage configuaration updated successfully')
        : '';
    } catch (error) {
      console.log('Error in fetching the Pass Percentage');
      percent
        ? showError('Error in changing the pass percentage configuration ')
        : '';
    }
  }
  useEffect(() => {
    getPercentage(null);
  }, []);
  return (
    <div className='space-y-6'>
      {/* Header Card */}
      <Card>
        <CardContent>
          <ScheduleHeader onAddClick={openAddDialog} />

          <ScheduleFilters
            departments={departments}
            onSearch={handleSearch}
            onClearFilters={handleClearFilters}
            instituteType={instituteType}
          />
          <ScheduleTable
            schedules={filteredSchedules}
            onEditSchedule={handleEditSchedule}
            onDeleteSchedule={handleDeleteSchedule}
            formatDate={formatDate}
            formatTime={formatTime}
            examTypes={examTypes}
            subjects={subjects}
            onScheduleDownload={downloadSchedule}
          />
        </CardContent>
      </Card>
      <PassPercentConfiguration
        passPercentage={passPercentage}
        modifyPassPercentage={getPercentage}
      />

      {/* Schedule Table Card */}
      {/* <Card>
        <CardContent>
          <ScheduleTable
            schedules={filteredSchedules}
            onEditSchedule={handleEditSchedule}
            onDeleteSchedule={handleDeleteSchedule}
            formatDate={formatDate}
            formatTime={formatTime}
            examTypes={examTypes}
            subjects={subjects}
            onScheduleDownload={downloadSchedule}
          />
        </CardContent>
      </Card> */}

      {/* Form Dialog */}
      <ScheduleFormDialog
        open={openDialog}
        onClose={() => {
          setClasses([]);
          setSections([]);
          setOpenDialog(false);
        }}
        onSubmit={
          dialogMode === 'add' ? handleAddSchedule : handleUpdateSchedule
        }
        passPercentage={passPercentage}
        mode={dialogMode}
        initialData={editingSchedule}
        examTypes={examTypes}
        subjects={subjects}
        departments={departments}
        classes={classes}
        sections={sections}
        onDepartmentChange={handleFormDepartmentChange}
        onClassChange={handleFormClassChange}
        hasConflictWithExisting={hasConflictWithExisting}
        Department={Department}
        instituteType={instituteType}
      />

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmationDialog.open}
        onClose={() =>
          setConfirmationDialog({
            open: false,
            title: '',
            message: '',
            onConfirm: null,
          })
        }
        onConfirm={confirmationDialog.onConfirm}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        confirmText='Delete'
        cancelText='Cancel'
        severity='error'
      />

      {/* Toast Notification */}
      <ToastNotification
        open={notification.open}
        onClose={hideNotification}
        message={notification.message}
        title={notification.title}
        severity={notification.severity}
        autoHideDuration={notification.autoHideDuration}
        position={notification.position}
      />
      <PrintPreviewSchedule
        schedule={printPreviewSchedule}
        open={printPreviewScheduleOpen}
        onClose={() => setPrintPreviewScheduleOpen(false)}
      />
    </div>
  );
};

export default ExamSchedule;
